import styled from '@emotion/styled'
import { Space } from 'antd'
import { isEqual, some, sum, uniqBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../../../../components/common/Button'
import Card from '../../../../components/common/Card'
import FooterContent from '../../../../components/FooterContent'
import { BACKGROUND } from '../../../../constant/colors'
import {
  BenefitsCategoryWrapperList,
  BenefitChoices,
  SelfBenefit,
} from '../../../../services/enrollment/enrollment-types'
import { mobile } from '../../../../utils/responsive-helper'
import BenefitsCategory from '../BenefitsCategory'
import { SelectedChoices } from './EditSelfBenefits'

export const ContentSpace = styled(Space)`
  width: 100%;
`

export const Layout = styled.div`
  margin-bottom: 40px;
`

export const BreadcrumbLayout = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`
export const Row = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`

export const Header = styled(Row)`
  justify-content: center;
  width: 100%;
  margin-bottom: 27px;
`

export const UserName = styled(Row)`
  flex-wrap: wrap;
  margin-bottom: 16px;

  .username {
    margin-right: 6px;
  }

  > *:not(:last-of-type) {
    margin-right: 8px;
  }
`

export const Content = styled(Card)`
  padding: 32px;
  .icon-enroll {
    margin-right: 16px;
  }
  ${mobile} {
    padding: 16px;
  }
`

export const ContentHeader = styled(Row)`
  margin-bottom: 16px;
`

export const ChoicesRadioTitle = styled(Row)`
  padding: 15px 24px;
  background-color: ${BACKGROUND};

  border: 1px solid ${BACKGROUND};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  .icon-insurance-title {
    margin-right: 12px;
    > {
      width: 32px;
      height: 32px;
    }
  }
`

export const RedeemButton = styled(Button)`
  width: 265px;
  height: 45px;
  margin-left: auto;
  ${mobile} {
    margin: auto;
    width: 100%;
  }
`

export const SummaryPoint = styled(Row)`
  flex-wrap: wrap;
  > *:not(:last-child) {
    margin-right: 8px;
  }

  ${mobile} {
    margin-bottom: 16px;
  }
`

export const FooterContentLayout = styled(FooterContent)`
  ${mobile} {
    flex-flow: column;
  }
`
export const BenefitsCategoryStyled = styled(BenefitsCategory)`
  .title-layout {
    margin-bottom: 16px;
    background-color: ${BACKGROUND};

    border: 1px solid ${BACKGROUND};
    border-radius: 8px;
  }
`

export const useSelectBenefitsHelper = (benefit?: SelfBenefit, userPoint = 0, isFetched = true) => {
  const [oldTotalBenefitsSelectedPoint, setOldTotalBenefitsSelectedPoint] = useState(0)
  const [totalBenefitsSelectedPoint, setTotalBenefitsSelectedPoint] = useState(0)
  const [remainPoint, setRemainPoint] = useState(0)

  const [oldSelectedChoices, setOldSelectedChoices] = useState<SelectedChoices[]>()
  const [selectedChoices, setSelectedChoices] = useState<SelectedChoices[]>([])

  const onSelectedChoices = useCallback(
    (benefitsCategory: BenefitsCategoryWrapperList, benefitsChoices: BenefitChoices) => {
      const { benefitsCategoryId, benefitsCategoryName } = benefitsCategory
      setSelectedChoices((prev) => {
        const benefitsChoicesIsExists = some(prev, (v) => v.id === benefitsChoices.id)
        if (!benefitsChoicesIsExists) {
          prev = prev.filter((v) => v.benefitsCategoryId !== benefitsCategoryId)
          prev.push({
            benefitsCategoryId,
            benefitsCategoryName,
            ...benefitsChoices,
          })
        }
        const _selectedChoices = uniqBy(prev, (v) => `${v.benefitsCategoryId}`)
        const _totalSelectedPoint =
          sum(
            _selectedChoices.map((v) => {
              const point = v.taggedPrice || v.defaultCoinValue || 0
              return v.selected ? point : point
            }),
          ) || 0
        const _remainPoint = userPoint + oldTotalBenefitsSelectedPoint - _totalSelectedPoint
        setTotalBenefitsSelectedPoint(_totalSelectedPoint)
        setRemainPoint(_remainPoint)

        return _selectedChoices
      })
    },
    [oldTotalBenefitsSelectedPoint, userPoint],
  )

  const isValuesNotChange = useMemo(() => {
    const oldSelected = oldSelectedChoices
      ? oldSelectedChoices.map((v) => `${v.benefitsCategoryId}-${v.id}`)
      : []
    const newSelected = selectedChoices.map((v) => `${v.benefitsCategoryId}-${v.id}`)
    const isLengthEqual = oldSelected.length === newSelected.length
    const isSelecteIdEqual = isEqual(oldSelected.sort(), newSelected.sort())
    return isLengthEqual && isSelecteIdEqual
  }, [oldSelectedChoices, selectedChoices])

  useEffect(() => {
    if (isFetched) {
      setSelectedChoices(() => {
        const selecteds = benefit
          ? benefit?.benefitsCategoryWrapperList.reduce(
              (acc: SelectedChoices[], { benefitsCategoryId, benefitsCategoryName, choices }) => {
                const choicesSelected = choices.find((v) => v.selected)
                if (choicesSelected) {
                  acc.push({
                    benefitsCategoryId,
                    benefitsCategoryName,
                    ...choicesSelected,
                  })
                }
                return acc
              },
              [],
            )
          : []

        const _totalSelectedPoint = sum(
          selecteds.map((v) => v.taggedPrice || v.defaultCoinValue || 0),
        )
        setOldSelectedChoices((prev) => (!prev ? selecteds : prev))
        setOldTotalBenefitsSelectedPoint(_totalSelectedPoint)
        setTotalBenefitsSelectedPoint(_totalSelectedPoint)
        setRemainPoint(userPoint)
        return selecteds
      })
    }
  }, [benefit, isFetched, userPoint])

  return {
    isValuesNotChange,
    remainPoint,
    oldTotalBenefitsSelectedPoint,
    totalBenefitsSelectedPoint,
    selectedChoices,
    onSelectedChoices,
  }
}
